import { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
  faTheaterMasks,
  faTimes,
  faUser,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import AwardBadge from '../../../badge';
import { useJuror } from '../../../../hooks/useAuth';
import { ROLES } from '../../../../config/permissions/roles';
import { matchRoles } from '../../../../helpers/roles';
import styles from './styles.module.scss';
import StatusIcon from '../../statusColumn/statusIcon';
import { useConfig } from '../../../../hooks/useConfig';

const ImpersonationAction = ({ onImpersonationClick, submission }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [groupMembers, setGroupMembers] = useState([]);
  const { juror, session } = useJuror();
  const { t } = useTranslation();
  const { AWARD_YEAR } = useConfig();

  useEffect(() => {
    if (!showDialog || !submission) return;

    setGroupMembers(
      submission.evaluations
        .filter((evaluation) =>
          matchRoles(evaluation.role, [ROLES.juror, ROLES.groupLeader])
        )
        .map((evaluation) => {
          return {
            jurorId: evaluation.jurorId,
            firstname: evaluation.firstname,
            lastname: evaluation.lastname,
            role: evaluation.role,
            profilePictureUrl: evaluation.profilePictureUrl,
            status: evaluation.status,
            score: evaluation.totalPoints,
            hasZeroValues: evaluation.hasZeroValues,
          };
        })
    );
  }, [setGroupMembers, showDialog, submission, juror]);

  const handleJurorClick = (jurorId) => {
    onImpersonationClick(
      `../entry/${submission.submissionId}${jurorId ? `/${jurorId}` : ''} `,
      submission
    );
  };

  return (
    <>
      {showDialog && (
        <div className={styles.backdrop} onClick={() => setShowDialog(false)}>
          <div className={styles.dialog} onClick={(e) => e.stopPropagation()}>
            <div
              className={styles.closeButton}
              onClick={() => setShowDialog(false)}
            >
              <FontAwesomeIcon icon={faTimes} className={styles.closeIcon} />
            </div>
            <div className={styles.header}>
              <AwardBadge
                awardType={session?.awardType}
                year={AWARD_YEAR}
                className={styles.badge}
              />
              <h1>
                {t(
                  'jurorEntries.table.columns.actions.impersonation.dialog.title'
                )}
              </h1>
              <p>
                <Trans
                  i18nKey="jurorEntries.table.columns.actions.impersonation.dialog.description"
                  components={{ italic: <i /> }}
                />
              </p>
            </div>
            <div className={styles.body}>
              <div className={styles.jurorList}>
                {groupMembers.map((juror) => (
                  <button
                    className={styles.jurorItem}
                    key={juror.jurorId}
                    onClick={() => handleJurorClick(juror.jurorId)}
                  >
                    <div className={styles.profilePicture}>
                      {juror.profilePictureUrl ? (
                        <img
                          src={juror.profilePictureUrl}
                          className={styles.img}
                          loading="lazy"
                        />
                      ) : (
                        <div className={styles.profileIconContainer}>
                          <FontAwesomeIcon
                            icon={faUser}
                            className={styles.icon}
                          />
                        </div>
                      )}
                    </div>
                    <span className={styles.jurorName}>
                      {juror.firstname} {juror.lastname}
                      <span className={styles.jurorRole}>{juror.role}</span>
                    </span>
                    <span className={styles.statusIcon}>
                      <StatusIcon
                        score={juror.score}
                        status={juror.status}
                        hasZeroValues={juror.hasZeroValues}
                      />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      <FontAwesomeIcon
        className={styles.impersonationIcon}
        icon={faTheaterMasks}
        size="2x"
        role="button"
        onClick={() => {
          setShowDialog(true);
        }}
      />
    </>
  );
};

export default ImpersonationAction;
